import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Building2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

export default function OrgProfilePage() {
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org/profile");
      setOrg(data.data.organization);
      const o = data.data.organization;
      setForm({
        website: o.website || "", alternate_mobile: o.alternate_mobile || "",
        address_line_2: o.address_line_2 || "",
        primary_contact_name: o.primary_contact_name || "",
        primary_contact_designation: o.primary_contact_designation || "",
        primary_contact_email: o.primary_contact_email || "",
        primary_contact_mobile: o.primary_contact_mobile || "",
        primary_color: o.primary_color || "#101876",
        secondary_color: o.secondary_color || "#329112",
        logo: o.logo || "",
      });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/org/profile", form);
      toast.success("Profile updated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading || !org) return <DashboardLayout title="Organization profile"><LoadingBlock label="Loading…" /></DashboardLayout>;

  return (
    <DashboardLayout title="Organization profile" breadcrumbs={[org.organization_name, "Profile"]}>
      <div className="max-w-4xl space-y-6" data-testid="org-profile-page">
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101876]/10 text-[#101876]"><Building2 className="h-6 w-6" /></div>
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{(org.organization_type || "").replace(/_/g, " ")}</div>
              <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{org.organization_name}</h2>
              <div className="mt-1 text-xs text-slate-500">{org.organization_code} · {org.city}, {org.state}</div>
            </div>
            <StatusBadge status={org.status} />
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            {[
              ["Legal name", org.legal_name || "—"],
              ["Registration #", org.registration_number || "—"],
              ["Timezone", org.timezone || "—"],
              ["Subscription plan", org.subscription_plan || "—"],
              ["Max users", org.max_users || "—"],
              ["Plan end", org.plan_end_date || "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</div>
                <div className="mt-1 text-slate-900">{v}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-slate-500">
            Locked fields (name, code, type, subscription, status) can only be changed by Steward support.
          </p>
        </section>

        <form onSubmit={submit} className="space-y-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#329112]">Editable fields</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <F label="Website"><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="org-profile-website" /></F>
              <F label="Alternate mobile"><Input value={form.alternate_mobile} onChange={(e) => setForm({ ...form, alternate_mobile: e.target.value })} data-testid="org-profile-alt-mobile" /></F>
              <F label="Address line 2"><Input value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} /></F>
              <F label="Logo URL"><Input value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} /></F>
              <F label="Contact person name"><Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} /></F>
              <F label="Designation"><Input value={form.primary_contact_designation} onChange={(e) => setForm({ ...form, primary_contact_designation: e.target.value })} /></F>
              <F label="Contact email"><Input type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} /></F>
              <F label="Contact mobile"><Input value={form.primary_contact_mobile} onChange={(e) => setForm({ ...form, primary_contact_mobile: e.target.value })} /></F>
              <F label="Primary color">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
                </div>
              </F>
              <F label="Secondary color">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
                  <Input value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} />
                </div>
              </F>
            </div>
          </section>
          <div className="flex justify-end">
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="org-profile-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save profile
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
